import { motion } from 'framer-motion';
import { BadgeCheck, Banknote, Briefcase, Clipboard, CheckCircle, FlaskConical, FolderOpen, RefreshCw, Search, Settings, Target, Users, Zap } from 'lucide-react';
import { Link } from 'react-router-dom';

const HRConsulting = () => {
  const services = [
    {
      id: 'fresher',
      icon: <Target size={26} />,
      title: 'Fresher Hiring Solutions',
      color: '#2b2240',
      gradient: 'linear-gradient(135deg, #2b2240, #4e3a69)',
      desc: 'End-to-end fresher recruitment for IT companies. We source, screen, assess, and deliver job-ready candidates who are trained in the latest tech stacks.',
      points: ['Campus & pool drives', 'Pre-trained tech talent', 'Bulk hiring support', 'Ready-to-deploy candidates', 'Zero notice period'],
    },
    {
      id: 'assessment',
      icon: <FlaskConical size={26} />,
      title: 'Technical Assessments',
      color: '#3b82f6',
      gradient: 'linear-gradient(135deg, #3b82f6, #2563eb)',
      desc: 'Structured technical evaluations using industry-standard platforms. Get accurate performance reports before making any hiring decision.',
      points: ['HackerRank test design', 'Coding round management', 'Face-to-face tech interviews', 'Skill-based scoring', 'Detailed performance reports'],
    },
    {
      id: 'talent',
      icon: <Briefcase size={26} />,
      title: 'Talent Acquisition',
      color: '#10b981',
      gradient: 'linear-gradient(135deg, #10b981, #059669)',
      desc: 'Full-cycle recruitment from job description to offer letter. We handle sourcing, screening, interviewing, and onboarding — saving you time and cost.',
      points: ['Job description crafting', 'Multi-channel sourcing', 'Resume & portfolio review', 'Interview coordination', 'Offer negotiation support'],
    },
    {
      id: 'process',
      icon: <Settings size={26} />,
      title: 'HR Process Consulting',
      color: '#F0B429',
      gradient: 'linear-gradient(135deg, #F0B429, #E8A317)',
      desc: 'Optimize your HR processes for efficiency, compliance, and employee satisfaction. We help you build HR systems that scale with your company.',
      points: ['Recruitment process design', 'Onboarding playbook', 'HR policy documentation', 'Performance review systems', 'Employee retention strategies'],
    },
  ];

  const hiringProcess = [
    { step: '01', icon: <Clipboard size={22} />, title: 'Requirements Gathering', desc: 'We understand your exact tech stack, culture, and team needs.' },
    { step: '02', icon: <Search size={22} />, title: 'Talent Sourcing', desc: 'Access our pool of trained, job-ready candidates.' },
    { step: '03', icon: <FlaskConical size={22} />, title: 'Screening & Assessment', desc: 'Technical tests, coding rounds, and skill evaluation.' },
    { step: '04', icon: <Users size={22} />, title: 'Interview Coordination', desc: 'We schedule and coordinate all interview rounds.' },
    { step: '05', icon: <CheckCircle size={22} />, title: 'Offer & Joining', desc: 'Offer letter support and smooth onboarding.' },
  ];

  const whyHireFromUs = [
    { icon: <Zap size={24} />, title: 'Pre-Trained Talent', desc: '60-day intensive training on your exact tech stack' },
    { icon: <FolderOpen size={24} />, title: 'Real Project Experience', desc: '5+ industry projects already in their portfolio' },
    { icon: <BadgeCheck size={24} />, title: 'Vetted & Assessed', desc: 'Tested via HackerRank and mock technical interviews' },
    { icon: <Target size={24} />, title: 'Zero Ramp-Up Time', desc: 'Ready to contribute from day one' },
    { icon: <Banknote size={24} />, title: 'Cost Effective', desc: 'Competitive packages with no placement agency markup' },
    { icon: <RefreshCw size={24} />, title: 'Replacement Guarantee', desc: 'Free replacement if candidate doesn\'t work out in 30 days' },
  ];

  const techStacks = ['Java + Spring Boot', 'Python + Django', 'Business Analytics', 'Data Analytics', 'Data Science', 'DevOps & Cloud'];

  const fadeUp = {
    hidden: { opacity: 0, y: 40 },
    visible: { opacity: 1, y: 0, transition: { duration: 0.6 } },
  };

  return (
    <div className="min-h-screen font-poppins" style={{ overflowX: 'hidden' }}>

      {/* ── HERO ──────────────────────────────────────────────────────── */}
      <section style={{
        background: 'linear-gradient(135deg, #0a0a1a 0%, #1a1530 40%, #2b2240 100%)',
        padding: '100px 1.5rem 80px',
        position: 'relative',
        overflow: 'hidden',
        textAlign: 'center',
      }}>
        <div style={{ position: 'absolute', inset: 0, backgroundImage: 'linear-gradient(rgba(255,255,255,0.03) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.03) 1px, transparent 1px)', backgroundSize: '60px 60px', pointerEvents: 'none' }} />
        <motion.div
          initial={{ opacity: 0, y: 40 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7 }}
          style={{ maxWidth: '820px', margin: '0 auto', position: 'relative', zIndex: 1 }}
        >
          <div style={{ display: 'inline-flex', alignItems: 'center', gap: '8px', background: 'rgba(240,180,41,0.15)', border: '1px solid rgba(240,180,41,0.35)', borderRadius: '50px', padding: '6px 20px', marginBottom: '24px', color: '#F0B429', fontSize: '0.875rem', fontWeight: '600' }}>
            <Briefcase size={15} /> HR Consulting
          </div>
          <h1 style={{ fontSize: 'clamp(2rem, 5vw, 3.5rem)', fontWeight: '900', color: 'white', lineHeight: 1.15, marginBottom: '20px' }}>
            Hire{' '}
            <span style={{ background: 'linear-gradient(135deg, #F0B429, #FF6535)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent', backgroundClip: 'text' }}>
              Pre-Trained Tech Talent
            </span>
            <br />That Delivers from Day One
          </h1>
          <p style={{ color: 'rgba(255,255,255,0.75)', fontSize: '1.1rem', lineHeight: 1.7, marginBottom: '36px' }}>
            Vista Tech HR Consulting connects growing IT companies with job-ready freshers and experienced talent. Our candidates come pre-trained, assessed, and ready to contribute immediately.
          </p>
          <div style={{ display: 'flex', gap: '16px', justifyContent: 'center', flexWrap: 'wrap' }}>
            <Link to="/contact" style={{ padding: '14px 28px', borderRadius: '12px', background: 'linear-gradient(135deg, #F0B429, #FF6535)', color: 'white', fontWeight: '700', textDecoration: 'none', boxShadow: '0 8px 24px rgba(240,180,41,0.4)' }}>
              Start Hiring Now
            </Link>
            <a href="#services" style={{ padding: '14px 28px', borderRadius: '12px', border: '1.5px solid rgba(255,255,255,0.3)', color: 'white', fontWeight: '600', textDecoration: 'none', background: 'rgba(255,255,255,0.06)' }}>
              Our Services
            </a>
          </div>

          <div style={{ display: 'flex', justifyContent: 'center', gap: '48px', marginTop: '60px', flexWrap: 'wrap' }}>
            {[['60', 'Days Training'], ['95%', 'Retention Rate']].map(([val, lbl]) => (
              <div key={lbl} style={{ textAlign: 'center' }}>
                <div style={{ fontSize: '2rem', fontWeight: '800', color: '#F0B429' }}>{val}</div>
                <div style={{ fontSize: '0.8rem', color: 'rgba(255,255,255,0.55)', marginTop: '4px' }}>{lbl}</div>
              </div>
            ))}
          </div>
        </motion.div>
      </section>

      {/* ── WHY HIRE FROM US ──────────────────────────────────────────── */}
      <section style={{ padding: '80px 1.5rem', background: 'white' }}>
        <div style={{ maxWidth: '1200px', margin: '0 auto' }}>
          <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={fadeUp} style={{ textAlign: 'center', marginBottom: '48px' }}>
            <h2 style={{ fontSize: 'clamp(1.6rem, 3.5vw, 2.5rem)', fontWeight: '800', color: '#2b2240', marginBottom: '12px' }}>
              Why Hire from Vista Tech?
            </h2>
            <p style={{ color: '#6b7280', fontSize: '1rem' }}>Our candidates are not just educated — they're work-ready</p>
          </motion.div>
          <div className="grid-3col" style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '20px' }}>
            {whyHireFromUs.map((w, i) => (
              <motion.div
                key={w.title}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.08 }}
                style={{ background: '#f8f7ff', borderRadius: '20px', padding: '28px', border: '1px solid rgba(43,34,64,0.06)' }}
              >
                <div style={{ display: 'inline-flex', padding: '12px', borderRadius: '14px', background: 'rgba(43,34,64,0.07)', color: '#2b2240', marginBottom: '16px' }}>{w.icon}</div>
                <h3 style={{ fontWeight: '700', color: '#2b2240', fontSize: '0.95rem', marginBottom: '8px' }}>{w.title}</h3>
                <p style={{ color: '#6b7280', fontSize: '0.85rem', lineHeight: 1.6 }}>{w.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* ── SERVICES ──────────────────────────────────────────────────── */}
      <section id="services" style={{ padding: '80px 1.5rem', background: '#f8f7ff' }}>
        <div style={{ maxWidth: '1200px', margin: '0 auto' }}>
          <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={fadeUp} style={{ textAlign: 'center', marginBottom: '48px' }}>
            <h2 style={{ fontSize: 'clamp(1.6rem, 3.5vw, 2.5rem)', fontWeight: '800', color: '#2b2240', marginBottom: '12px' }}>
              Our HR Services
            </h2>
          </motion.div>
          <div className="grid-2col" style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '24px' }}>
            {services.map((s, i) => (
              <motion.div
                key={s.id}
                id={s.id}
                initial={{ opacity: 0, y: 40 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.1 }}
                style={{ background: 'white', borderRadius: '24px', overflow: 'hidden', boxShadow: '0 4px 20px rgba(43,34,64,0.07)', border: '1px solid rgba(43,34,64,0.05)' }}
              >
                <div style={{ height: '4px', background: s.gradient }} />
                <div style={{ padding: '32px' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '14px', marginBottom: '16px' }}>
                    <span style={{ display: 'inline-flex', padding: '12px', borderRadius: '14px', background: `${s.color}12`, color: s.color, flexShrink: 0 }}>{s.icon}</span>
                    <h3 style={{ fontSize: '1.1rem', fontWeight: '800', color: '#2b2240' }}>{s.title}</h3>
                  </div>
                  <p style={{ color: '#6b7280', fontSize: '0.875rem', lineHeight: 1.65, marginBottom: '20px' }}>{s.desc}</p>
                  <ul style={{ listStyle: 'none', padding: 0, margin: 0, display: 'flex', flexDirection: 'column', gap: '8px' }}>
                    {s.points.map((pt) => (
                      <li key={pt} style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '0.85rem', color: '#4b5563' }}>
                        <span style={{ width: '18px', height: '18px', borderRadius: '50%', background: s.gradient, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                          <svg width="9" height="9" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="3"><path d="M20 6L9 17l-5-5" /></svg>
                        </span>
                        {pt}
                      </li>
                    ))}
                  </ul>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* ── TECH STACKS ───────────────────────────────────────────────── */}
      <section style={{ padding: '60px 1.5rem', background: 'white' }}>
        <div style={{ maxWidth: '900px', margin: '0 auto', textAlign: 'center' }}>
          <p style={{ color: '#9ca3af', fontSize: '0.875rem', fontWeight: '600', letterSpacing: '0.1em', marginBottom: '24px', textTransform: 'uppercase' }}>We Source Talent For These Tech Stacks</p>
          <div style={{ display: 'flex', flexWrap: 'wrap', justifyContent: 'center', gap: '12px' }}>
            {techStacks.map((t) => (
              <span key={t} style={{ padding: '8px 20px', borderRadius: '50px', background: '#f8f7ff', color: '#4e3a69', fontSize: '0.875rem', fontWeight: '600', border: '1px solid rgba(78,58,105,0.12)' }}>{t}</span>
            ))}
          </div>
        </div>
      </section>

      {/* ── HIRING PROCESS ────────────────────────────────────────────── */}
      <section style={{ padding: '80px 1.5rem', background: '#f8f7ff' }}>
        <div style={{ maxWidth: '1100px', margin: '0 auto' }}>
          <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={fadeUp} style={{ textAlign: 'center', marginBottom: '48px' }}>
            <h2 style={{ fontSize: 'clamp(1.6rem, 3.5vw, 2.4rem)', fontWeight: '800', color: '#2b2240', marginBottom: '12px' }}>
              Our Hiring Process
            </h2>
            <p style={{ color: '#6b7280' }}>From requirement to onboarding in as little as 7 days</p>
          </motion.div>
          <div className="grid-5col-process" style={{ display: 'grid', gridTemplateColumns: 'repeat(5, 1fr)', gap: '16px' }}>
            {hiringProcess.map((p, i) => (
              <motion.div
                key={p.step}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.1 }}
                style={{ textAlign: 'center', padding: '24px 16px', background: 'white', borderRadius: '20px', boxShadow: '0 2px 16px rgba(43,34,64,0.07)', position: 'relative' }}
              >
                {i < hiringProcess.length - 1 && (
                  <div style={{ position: 'absolute', top: '36px', right: '-8px', fontSize: '1rem', color: '#d1d5db', zIndex: 1 }}>→</div>
                )}
                <div style={{ display: 'flex', justifyContent: 'center', marginBottom: '12px', color: '#2b2240' }}>{p.icon}</div>
                <div style={{ fontSize: '0.7rem', fontWeight: '800', color: '#2b2240', letterSpacing: '0.08em', marginBottom: '6px' }}>STEP {p.step}</div>
                <h3 style={{ fontSize: '0.875rem', fontWeight: '700', color: '#2b2240', marginBottom: '8px' }}>{p.title}</h3>
                <p style={{ color: '#6b7280', fontSize: '0.775rem', lineHeight: 1.5 }}>{p.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* ── CTA ───────────────────────────────────────────────────────── */}
      <section style={{ padding: '80px 1.5rem', background: 'linear-gradient(135deg, #2b2240, #4e3a69)', textAlign: 'center' }}>
        <div style={{ maxWidth: '700px', margin: '0 auto' }}>
          <motion.div initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}>
            <h2 style={{ fontSize: 'clamp(1.6rem, 4vw, 2.5rem)', fontWeight: '800', color: 'white', marginBottom: '16px' }}>
              Looking to Hire Tech Talent?
            </h2>
            <p style={{ color: 'rgba(255,255,255,0.75)', fontSize: '1rem', lineHeight: 1.7, marginBottom: '32px' }}>
              Tell us your requirements and we'll match you with pre-trained, job-ready candidates in as little as 7 days.
            </p>
            <div style={{ display: 'flex', gap: '16px', justifyContent: 'center', flexWrap: 'wrap' }}>
              <Link to="/contact" style={{ padding: '14px 28px', borderRadius: '12px', background: 'linear-gradient(135deg, #F0B429, #FF6535)', color: 'white', fontWeight: '700', textDecoration: 'none', boxShadow: '0 8px 24px rgba(240,180,41,0.35)' }}>
                Submit Hiring Requirement
              </Link>
              <Link to="/contact" style={{ padding: '14px 28px', borderRadius: '12px', border: '1.5px solid rgba(255,255,255,0.3)', color: 'white', fontWeight: '600', textDecoration: 'none' }}>
                Schedule a Call
              </Link>
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default HRConsulting;
